import { createAsyncThunk } from "@reduxjs/toolkit";
import { getPrefix } from "features/Application/slices/models";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "../../../../../enums/reducer";
import accountsDataService from "../../../services/accountsDataService";
import { type AddContactPayload } from "interfaces/contact";
import type { DistributedOpUpdateParams } from "interfaces";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type AccountConfigurationInfo } from "features/Accounts/types";
import type NewContact from "interfaces/NewContact";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { isEmpty } from "lodash";

export const accountTypesPrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "accountTypes",
};

export const fetchAccountTypes = createAsyncThunk(getPrefix(accountTypesPrefixPayload), async () => {
  const items = await accountsDataService.getAccountTypes();
  return { items, totalCount: items.length };
});

export const globalRolesStatePrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "globalRoles",
};

export const childRolesStatePrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "childRoles",
};

export const createAccountPrefixPayload = {
  namespace: ReducerNamespaceTypes.Accounts,
  entity: ReducerEntityPrefixTypes.AccountCreation,
  name: "createAccount",
};

export const fetchGlobalRoles = createAsyncThunk(
  getPrefix(globalRolesStatePrefixPayload),
  async (accountTypeId: number) => {
    const items = await accountsDataService.getAccountTypeRoles(accountTypeId);
    return { items, totalCount: items.length };
  },
);

export const fetchChildAccountRoles = createAsyncThunk(
  getPrefix(childRolesStatePrefixPayload),
  async (accountId: number) => {
    const items = await accountsDataService.getChildAccountRoles(accountId);
    return { items, totalCount: items.length };
  },
);

export const addManagedByContactsThunk = createAsyncThunk(
  "accounts/addManagedByContacts",
  async (payload: AddContactPayload) => {
    try {
      const response = await accountsDataService.addManagedByContacts(payload);
      return response;
    } catch (error) {}
  },
);

export const createAccountThunk = createAsyncThunk(
  getPrefix(createAccountPrefixPayload),
  async ({ account, contacts }: { account: AccountConfigurationInfo; contacts: NewContact[] }, { dispatch }) => {
    const params: DistributedOpUpdateParams = {
      id: "CreateAccount",
      title: `Creating '${account.name}' account`,
      indeterminate: true,
      getOperationProps: async () => {
        return accountsDataService.createAccount({
          ...account,
          alias: isEmpty(account.alias) ? null : account.alias,
          contractTypeId: account.contractTypeId === -1 ? null : account.contractTypeId,
          contacts: contacts.map((contact: NewContact) => {
            const { roles, title, ...contactInfo } = contact;

            return {
              ...contactInfo,
              roleIds: roles ? roles.map((x) => x.id) : [],
              jobTitle: title,
            };
          }),
          timeZone: isEmpty(account.timeZone) ? null : account.timeZone,
        });
      },
      successTransientMessage: "Account has been created successfully.",
      failureTransientMessage: "Account creation failed!",
    };

    await backgroundTask.initializeDistributedBackgroundTask(params, dispatch as AppDispatch);
  },
);
